import { ThemedIcon } from "@src/common/ThemedIcon/ThemedIcon";
import { ATHENA_PATIENT_LOGIN_URL } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useThemeContext } from "@src/context/ThemeContext";
import type { ThemeIconName } from "@src/interfaces/theme-config.interface";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { createTheme } from "@uhg-abyss/web/tools/theme";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Divider } from "@uhg-abyss/web/ui/Divider";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import type { ValidIconBrandName } from "@uhg-abyss/web/ui/IconBrand";
import { Text } from "@uhg-abyss/web/ui/Text";
import { ThemeProvider } from "@uhg-abyss/web/ui/ThemeProvider";

const IntroBody = styled(Text, {
  color: "$app.color.get.started.sub.header.text",
  margin: "0",
  textAlign: "center",
});

const BenefitsList = styled("ul", {
  display: "flex",
  fontWeight: "700",
  gap: "12px",
  listStyle: "none",
  margin: "16px auto",
  padding: "0 16px",
  justifyContent: "space-between",
  width: "100%",
  variants: {
    isMobile: {
      true: {
        margin: "8px auto",
        padding: "0",
        alignItems: "center",
        flexDirection: "column",
        gap: "8px",
      },
    },
  },
});

const ScheduleCareText = styled(Text, {
  color: "$app.color.get.started.new.patient.text",
  fontSize: "24px",
  fontWeight: "700",
  paddingBottom: "8px",
  paddingLeft: "4px",
  textAlign: "center",
  width: "100%",
  variants: {
    isMobile: {
      true: {
        width: "100%",
        fontSize: "21px",
      },
    },
  },
});

const ExistingPatientSection = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  variants: {
    isMobile: {
      true: {
        gap: "20px",
      },
    },
  },
});

const ExistingPatientButtonHolder = styled("div", {
  display: "flex",
  justifyContent: "center",
});

const Benefit = styled("li", {
  alignItems: "center",
  color: "$app.color.get.started.sub.header.text",
  display: "flex",
  gap: "4px",
  variants: {
    isMobile: {
      true: {
        gap: "4px",
      },
    },
  },
});

const IntroHolder = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const SerifEmphasis = styled("em", {
  fontFamily: "$web.semantic.font-family.h-serif",
  fontSize: "38px",
  variants: {
    isMobile: {
      true: {
        fontSize: "30px",
      },
    },
  },
});

const OPTUM_REFRESH_BUTTON_THEME = createTheme("optum-refresh");

const BENEFITS: Array<{
  icon: ThemeIconName & ValidIconBrandName;
  text: string;
}> = [
  { icon: "location_pin", text: "Care near you" },
  { icon: "desktop", text: "Virtual & in-person" },
  { icon: "clipboard_checked", text: "Most insurance accepted" },
];

export const GetStartedIntro = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);

  const { activeThemeName } = useThemeContext();
  // The ableto theme keeps the default button color.
  const isAbleTo = activeThemeName === "ableto";

  const existingPatientButton = (
    <ThemeProvider theme={isAbleTo ? undefined : OPTUM_REFRESH_BUTTON_THEME}>
      <Button
        id="obc-get-started-athena-login-btn"
        data-testid="test-id-get-started-existing-patients-button"
        data-obc-label="Existing Patients Athena Login Button"
        size="md"
        color={isAbleTo ? undefined : "destructive"}
        onClick={() => {
          window.location.href = ATHENA_PATIENT_LOGIN_URL;
        }}
      >
        Existing Patients Click Here
      </Button>
    </ThemeProvider>
  );

  return (
    <IntroHolder data-testid="test-id-get-started-intro">
      <Heading
        color="$app.color.get.started.header.text"
        data-testid="test-id-get-started-intro-heading"
        textAlign="center"
        css={{
          fontSize: isMobile ? "28px" : "36px",
          lineHeight: isMobile ? "36px" : "48px",
          marginBottom: "8px",
          padding: "0 16px",
        }}
      >
        Hope, help and
        <br />
        healing{" "}
        <SerifEmphasis data-testid="test-id-get-started-intro-heading-emphasis" isMobile={isMobile}>
          start here
        </SerifEmphasis>
        .
      </Heading>
      <IntroBody
        data-testid="test-id-get-started-intro-body"
        css={{
          fontSize: isMobile ? "16px" : "18px",
        }}
      >
        Quality behavioral health care for everyone, of all ages.
      </IntroBody>
      <BenefitsList
        aria-label="Benefits"
        data-testid="test-id-get-started-benefits"
        isMobile={isMobile}
      >
        {BENEFITS.map(({ icon, text }) => (
          <Benefit
            data-testid={`test-id-get-started-benefit-${icon}`}
            key={icon}
            isMobile={isMobile}
          >
            <ThemedIcon
              themeIconName={icon}
              themedIconStyle={{ flexShrink: 0, height: "24px", width: "24px" }}
              themedImgProps={{
                "aria-hidden": true,
              }}
              iconBrandProps={{
                "aria-hidden": true,
                brand: "optum-refresh",
                css: { flexShrink: 0 },
                "data-testid": `test-id-get-started-benefit-icon-${icon}`,
                icon,
                size: "24",
              }}
            />
            <Text
              data-testid={`test-id-get-started-benefit-text-${icon}`}
              fontWeight="bold"
              fontSize={isMobile ? "sm" : "md"}
            >
              {text}
            </Text>
          </Benefit>
        ))}
      </BenefitsList>
      <ExistingPatientSection isMobile={isMobile}>
        <Divider color="$app.color.get.started.divider" />
        <ExistingPatientButtonHolder>{existingPatientButton}</ExistingPatientButtonHolder>
        <Divider color="$app.color.get.started.divider" />
      </ExistingPatientSection>
      <ScheduleCareText data-testid="test-id-get-started-new-patient-text" isMobile={isMobile}>
        <em>New patient? Start here.</em>
      </ScheduleCareText>
    </IntroHolder>
  );
};
